// Quiz transitions — applies leave/enter classes when WPProQuiz changes
// the active question, so CSS can drive the per-style transitions.
//
// LearnDash's WPProQuiz JS toggles `display:list-item` on each
// `.wpProQuiz_listItem` (one per question) when the user advances. We
// observe `style.display` mutations on those items via MutationObserver,
// then add `.is-leaving` to the outgoing one and `.is-entering` to the
// incoming one. CSS handles the actual transitions.
//
// Also builds and keeps in sync the design's single progress bar —
// `.bnrmp-quiz__progress-row` > `.bnrmp-progress` + caption
// (module-1-quiz.html:158). This comment previously named
// `.bnrmp-quiz__progress-dot`, the pre-port SEGMENTED DOT STRIP that the
// design replaced; that class was retired 2026-08-17 and the reference here
// was stale.

export function initQuizTransitions() {
  const quizzes = document.querySelectorAll('.bnrmp-quiz');
  if (!quizzes.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  quizzes.forEach((quiz) => {
    const list = quiz.querySelector('.wpProQuiz_list');
    if (!list) {
      // WPProQuiz hasn't rendered yet — defer.
      retryUntilReady(quiz, attachToList, 12);
      return;
    }
    attachToList(quiz, list, reduced);
  });
}

function retryUntilReady(quiz, fn, attempts) {
  if (attempts <= 0) return;
  setTimeout(() => {
    const list = quiz.querySelector('.wpProQuiz_list');
    if (list) {
      fn(quiz, list, window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } else {
      retryUntilReady(quiz, fn, attempts - 1);
    }
  }, 250);
}

function attachToList(quiz, list, reduced) {
  const items = Array.from(list.querySelectorAll('.wpProQuiz_listItem'));
  if (!items.length) return;

  // Tag with index for the chrome.
  items.forEach((item, i) => item.setAttribute('data-bnrmp-q-index', String(i)));

  // Inject progress strip if not present.
  ensureProgressStrip(quiz, items.length);
  syncProgressStrip(quiz, items);

  // 🔴 ANSWER GATE — DELIBERATELY ABOVE THE `reduced` EARLY-RETURN.
  // Everything below that return is MOTION and is legitimately skipped for
  // prefers-reduced-motion. This is BEHAVIOUR: a learner on reduced motion
  // must get the same gate, or the control differs by OS setting.
  items.forEach(gateAdvanceUntilAnswered);

  if (reduced) {
    // Skip motion classes entirely — CSS short-circuits anyway, but we
    // also skip the observer to keep the page light.
    return;
  }

  // Observe each item's inline display style.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type !== 'attributes' || m.attributeName !== 'style') return;
      const el = m.target;
      // Same measured correction as isVisible() below: WPProQuiz reveals a
      // question by REMOVING its inline `display:none`, so the inline value
      // is an empty string on the visible item, not "block". The old
      // truthiness test was never true, which meant this branch — and with
      // it the syncProgressStrip() call that keeps the progress bar and its
      // aria-valuenow current — never ran on advance.
      const shown = isVisible(el);
      if (shown) {
        // Entering — make sure leaving class is cleared.
        items.forEach((other) => {
          if (other !== el) other.classList.remove('is-entering');
        });
        el.classList.remove('is-leaving');
        el.classList.add('is-entering');
        syncProgressStrip(quiz, items);
      } else {
        // Hidden — outgoing.
        if (el.classList.contains('is-entering')) {
          el.classList.add('is-leaving');
          el.classList.remove('is-entering');
        }
      }
    });
  });

  items.forEach((item) => {
    observer.observe(item, { attributes: true, attributeFilter: ['style'] });
  });

  // Mark first visible item as entering on load.
  const firstVisible = items.find(isVisible);
  if (firstVisible) firstVisible.classList.add('is-entering');
}

// ── ADVANCE GATE (2026-08-20, operator: "The next button is only clickable
// after selected an ans.") ────────────────────────────────────────────────
//
// LearnDash has NO native setting for this. `skip_question_disabled` is set
// to 1 on all three quizzes and looks like the right switch, but it only
// governs whether a separate **Skip** button is rendered
// (wpProQuiz_front.js:1871-1874) — it does not gate Next, and Next was
// measured `disabled:false, cursor:pointer` with nothing selected.
//
// Enforced by DISABLING the button rather than by intercepting the click:
// `disabled` is what the design's own disabled treatment keys off
// (`.bnrmp-btn--primary:disabled`, main.css:1942), it removes the control
// from the tab order, and screen readers announce it — where a swallowed
// click is silent and reads as a broken button.
//
// Scoped to `[name="next"]`. `back` must stay live so a learner can always
// go back, and `check`/`skip` are LD's own controls with their own rules.
function gateAdvanceUntilAnswered(item) {
  const next = item.querySelector('input[name="next"]');
  if (!next) return;

  const inputs = Array.from(
    item.querySelectorAll('.wpProQuiz_questionListItem input')
  ).filter((el) => el.type === 'radio' || el.type === 'checkbox');

  // Question types with no radio/checkbox (free-text, sorting, matrix) are
  // left alone — this gate has no reliable "answered" signal for them and a
  // wrong guess would strand the learner on the question.
  if (!inputs.length) return;

  const sync = () => {
    const answered = inputs.some((el) => el.checked);
    next.disabled = !answered;
    // LD re-renders the value/attrs on advance; keep the a11y hint in step.
    next.setAttribute('aria-disabled', answered ? 'false' : 'true');
  };

  inputs.forEach((el) => {
    el.addEventListener('change', sync);
    // `change` does not fire when the SAME radio is re-clicked, and LD
    // restores a previous answer when the learner navigates Back — so sync
    // on click too, and once now for that restored-state case.
    el.addEventListener('click', sync);
  });

  sync();
}

// Strings come from PHP (wp_localize_script in inc/child_learndash.php) so
// they live in the .po catalogue. The fallbacks are English and are only
// reached if the localize call is ever removed.
const PROGRESS_I18N = {
  label: 'Quiz progress',
  // %1$d = current question, %2$d = total, %3$d = percent complete.
  caption: 'Question %1$d of %2$d, %3$d%% complete',
};

function progressI18n() {
  const injected = typeof window !== 'undefined' ? window.bnrmpQuizI18n : null;
  return { ...PROGRESS_I18N, ...(injected || {}) };
}

function formatCaption(tpl, current, total, pct) {
  // `%%` is a printf escape for a literal `%`. PHP's __() does NOT collapse
  // it — only printf does — so the string arrives here still doubled and has
  // to be unescaped last, after the numbered placeholders are filled.
  // Measured: the caption rendered "0%% complete" before this line existed.
  return String(tpl)
    .replace('%1$d', current)
    .replace('%2$d', total)
    .replace('%3$d', pct)
    .replace(/%%/g, '%');
}

/*
 * Which question is on screen.
 *
 * MEASURED, not assumed: WPProQuiz reveals a question by REMOVING the inline
 * `display:none` it set, which leaves `el.style.display` an EMPTY STRING —
 * not `"block"`. A truthiness test on the inline style therefore reports
 * "nothing is visible" for the one item that is, which is why the progress
 * bar sat at 0% and the caption read "Question 0 of 3" on every question.
 * Asking the layout engine answers the question the user's eyes ask.
 */
function isVisible(el) {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

/*
 * The design's progress element (module-1-quiz.html:158-165) is ONE 12px
 * bordered track with its percentage caption on the same row — not the
 * segmented dot strip this module used to build.
 *
 * The strip was a real `role="progressbar"` with a maintained `aria-valuenow`
 * that a screen reader announces, so it is REPLACED, not deleted: the new
 * track carries the same role, the same aria-label, and a `aria-valuenow`
 * maintained on the same schedule — now on the design's 0-100 percentage
 * scale, which is what `aria-valuemin/max` here declare. The caption is a
 * real localised string, and is aria-hidden because the progressbar already
 * announces the same information.
 */
function ensureProgressStrip(quiz, total) {
  if (quiz.querySelector('.bnrmp-quiz__progress-row')) return;
  const chrome = quiz.querySelector('.bnrmp-quiz__chrome');
  if (!chrome) return;

  const i18n = progressI18n();

  const row = document.createElement('div');
  row.className = 'bnrmp-quiz__progress-row';

  const track = document.createElement('div');
  track.className = 'bnrmp-progress';
  track.setAttribute('role', 'progressbar');
  track.setAttribute('aria-label', i18n.label);
  track.setAttribute('aria-valuemin', '0');
  track.setAttribute('aria-valuemax', '100');
  track.setAttribute('aria-valuenow', '0');

  const fill = document.createElement('div');
  fill.className = 'bnrmp-progress__fill';
  fill.style.width = '0%';
  track.appendChild(fill);

  const caption = document.createElement('p');
  caption.className = 'bnrmp-quiz__progress-caption';
  // The progressbar above already conveys this to assistive tech; announcing
  // it twice would be noise.
  caption.setAttribute('aria-hidden', 'true');
  caption.dataset.bnrmpTotal = String(total);

  row.appendChild(track);
  row.appendChild(caption);
  chrome.appendChild(row);
}

function syncProgressStrip(quiz, items) {
  const row = quiz.querySelector('.bnrmp-quiz__progress-row');
  if (!row) return;
  const track = row.querySelector('.bnrmp-progress');
  const fill = row.querySelector('.bnrmp-progress__fill');
  const caption = row.querySelector('.bnrmp-quiz__progress-caption');

  let activeIndex = -1;
  items.forEach((it, i) => {
    if (isVisible(it)) activeIndex = i;
  });

  // 🔴 NO QUESTION VISIBLE = THE QUIZ HAS NOT STARTED. Hide the whole row.
  //
  // MEASURED 2026-08-17: on the pre-start screen (the "Start Quiz" button
  // showing) activeIndex stays -1, so `current` fell to 0 and the row rendered
  // "Question 0 of 3, 0% complete" with role="progressbar" aria-valuenow="0",
  // at both viewports. There is no question zero — the string is user-visible
  // nonsense, and announcing progress on a screen where the quiz has not begun
  // is wrong for assistive tech too. The design has no pre-start screen at all;
  // its progress row exists only inside the started form
  // (module-1-quiz.html:158), so there is nothing to render here.
  //
  // Why this was missed until a verifier caught it: the earlier check measured
  // captions post-start (0 -> 33 -> 67) and never looked at the state before
  // the first question appears. Measuring the states you thought of is not the
  // same as measuring the states a learner passes through.
  const started = activeIndex >= 0;
  row.hidden = !started;
  if (!started) return;

  const total = items.length;
  const current = Math.max(activeIndex + 1, 0);
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  if (fill) fill.style.width = pct + '%';
  if (track) track.setAttribute('aria-valuenow', String(pct));
  if (caption) {
    caption.textContent = formatCaption(progressI18n().caption, current, total, pct);
  }
}
